/** @format */

import { useRef, useMemo } from "react";
import clsx from "clsx";
import { useImmer } from "use-immer";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
// import { Prompt } from "react-router-dom";
import { InputTextarea } from "primereact/inputtextarea";
import { DragDropContext, Droppable } from "react-beautiful-dnd";

import { useState, useEffect, useCallback } from "react";
import omit from "lodash.omit";
import { useParams, useNavigate } from "react-router-dom";
import ThreeDotsLoader from "../common/ThreeDotsLoader";
import useGetSurveyById from "./hooks/useGetSurveyById";
import useUpdateSurvey from "./hooks/useUpdateSurvey";
import usePublishSurvey from "./hooks/usePublishSurvey";
import useCheckActiveSurvey from "./hooks/useCheckActiveSurvey";
import { SURVEY } from "./constants/constants";
import { useQueryClient } from "@tanstack/react-query";
import { getQuestion, isSurveyInvalid } from "./utils/utils";
import QuestionTemplate from "./questionTemplates/QuestionTemplate";
import Preview from "./Preview";

const SurveyForm = (props) => {
  const [survey, setSurvey] = useImmer({
    surveyId: -1,
    title: "",
    need: "",
    status: "",
    description: "",
    questionRequestDTOS: [getQuestion()],
  });

  let toastRef = useRef(null);

  const navigate = useNavigate();

  const [showPreview, setShowPreview] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isBackHandler, setIsBackHandler] = useState(false);

  let { id } = useParams();

  const { data, isLoading, refetch: refetchSurveyById } = useGetSurveyById(id);
  const publishCheckQuery = useCheckActiveSurvey(false);
  const publishMutation = usePublishSurvey(id);
  const mutation = useUpdateSurvey();
  const queryClient = useQueryClient();

  let { status } = survey;
  const isActive = useMemo(() => status.toLocaleLowerCase() === SURVEY.STATUS.ACTIVE.toLocaleLowerCase(), [status]);
  const isInactive = useMemo(() => status.toLocaleLowerCase() === SURVEY.STATUS.INACTIVE.toLocaleLowerCase(), [status]);
  const isInProgress = useMemo(
    () => status.toLocaleLowerCase() === SURVEY.STATUS.IN_PROGRESS.toLocaleLowerCase(),
    [status]
  );

  useEffect(() => {
    if (data !== null && data !== undefined) {
      let questionRequestDTOS = [getQuestion()];
      if (Array.isArray(data.questionRequestDTOS)) {
        questionRequestDTOS = data.questionRequestDTOS.map((questionRequestDTO) => {
          let updatedQuestionDTO = omit(questionRequestDTO, "questionId");
          if (Array.isArray(updatedQuestionDTO.optionRequestDTOS)) {
            updatedQuestionDTO.optionRequestDTOS = updatedQuestionDTO.optionRequestDTOS.map((optionRequestDTO) =>
              omit(optionRequestDTO, "optionId")
            );
          } else {
            updatedQuestionDTO.optionRequestDTOS = [];
          }
          return updatedQuestionDTO;
        });
      }
      setSurvey((draft) => {
        draft.surveyId = data.surveyId;
        draft.title = data.title;
        draft.description = data.description;
        draft.need = data.need;
        draft.questionRequestDTOS = questionRequestDTOS;
        draft.status = data.status;
      });
    }
  }, [setSurvey, data]);

  const backHandler = useCallback(() => {
    setIsBackHandler(true);
  }, []);
  const saveSurvey = useCallback(
    (survey) => {
      return mutation.mutateAsync(survey, {
        onSuccess: () => {
          toastRef.current.show({
            severity: "success",
            summary: "Success",
            detail: "Survey updated successfully",
            life: 1000,
          });
          queryClient.invalidateQueries({
            queryKey: ["getSurveyById", id],
          });
        },
        onError: () => {
          toastRef.current.show({
            severity: "error",
            summary: "Error",
            detail: "Error updating Survey",
            life: 1000,
          });
        },
      });
    },
    [mutation, id, queryClient]
  );
  const publishSurvey = useCallback(
    async (survey) => {
      try {
        if (isSurveyInvalid(survey.questionRequestDTOS)) {
          setShowValidation(true);
        } else {
          setShowValidation(false);
          await saveSurvey(survey);
          publishCheckQuery
            .refetch({ throwOnError: true })
            .then((res) => {
              if (res.data) {
                toastRef.current.show({
                  severity: "error",
                  summary: "Active survey exists",
                  detail: "Active Survey existing, cannot publish",
                  life: 2000,
                });
              } else {
                publishMutation.mutate(id, {
                  onSuccess: () => {
                    toastRef.current.show({
                      severity: "success",
                      summary: "Success",
                      detail: "Survey published successfully",
                      life: 1000,
                    });
                    queryClient.invalidateQueries({
                      queryKey: ["getAllSurvey"],
                    });
                    refetchSurveyById();
                  },
                  onError: () => {
                    toastRef.current.show({
                      severity: "error",
                      summary: "Error",
                      detail: "Error publishing Survey",
                      life: 1000,
                    });
                  },
                });
              }
            })
            .catch((err) => {
              console.error(err);
              toastRef.current.show({
                severity: "error",
                summary: "Error",
                detail: "surveyExistence API Error",
                life: 1000,
              });
            });
        }
      } catch (err) {
        console.error("ERROR", err);
      }
    },
    [publishMutation, id, queryClient, publishCheckQuery, saveSurvey, refetchSurveyById]
  );
  const onDescriptionChangeHandler = useCallback(
    (e) => {
      let value = e.target.value;
      setSurvey((draft) => {
        draft.description = value;
      });
    },
    [setSurvey]
  );
  const onQuestionDragEndHandler = useCallback(
    (result) => {
      let startIndex = result.source && result.source.index;
      let endIndex = result.destination && result.destination.index;
      setSurvey((draft) => {
        if (startIndex && endIndex) {
          let [removed] = draft.questionRequestDTOS.splice(startIndex, 1);
          draft.questionRequestDTOS.splice(endIndex, 0, removed);
        }
      });
    },
    [setSurvey]
  );
  const questionsLength = survey.questionRequestDTOS.length;
  return (
    <>
      <Toast ref={toastRef} baseZIndex={20000} />
      {/* <Prompt
        when={!isBackHandler}
        message={(location, action) => {
          return "Are you sure you want to leave";
        }}
      /> */}
      {isLoading || publishMutation.isLoading || mutation.isLoading || publishCheckQuery.isFetching ? (
        <div className='wtg__loader'>
          <ThreeDotsLoader />
        </div>
      ) : null}
      <div className='flex align-items-center'>
        <label className='font-bold font-size-18 line-height-36 light-grey mr-3'>{survey.title}:</label>
        <label className='light-grey'>{survey.need}</label>
        <Button
          label='Back'
          className='ml-auto survey__form__back__button'
          icon='pi pi-arrow-left'
          iconPos='left'
          onClick={backHandler}
        />
      </div>
      <p>
        <label className='font-bold font-size-18 line-height-36 light-grey mr-3'>Status:</label>
        <span
          className={clsx({
            survey__active: isActive,
            survey__inprogress: isInProgress,
            survey__inactive: isInactive,
          })}
        >
          {survey.status}
        </span>
      </p>
      <div className='p-4' id='survey-form'>
        <div className='p-fluid'>
          <div className='field'>
            <label htmlFor='firstname1'>
              Survey Description<sup>*</sup>
            </label>
            <InputTextarea value={survey.description} onChange={onDescriptionChangeHandler} />
          </div>
        </div>
        <DragDropContext onDragEnd={onQuestionDragEndHandler}>
          <Droppable droppableId='question-droppable'>
            {(provided, snapshot) => {
              return (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {survey.questionRequestDTOS.map((question, questionIndex) => {
                    let optionRequestDTOS = question.optionRequestDTOS ?? [];
                    let answerLength = optionRequestDTOS.length;
                    const isMultipleChoice = question.type === SURVEY.QUESTION__TYPES.MULTIPLE__CHOICE;
                    const isShortAnswer = question.type === SURVEY.QUESTION__TYPES.SHORT_ANSWER;
                    return (
                      <QuestionTemplate
                        key={`draggable-${questionIndex}`}
                        questionIndex={questionIndex}
                        optionRequestDTOS={optionRequestDTOS}
                        answerLength={answerLength}
                        isMultipleChoice={isMultipleChoice}
                        isShortAnswer={isShortAnswer}
                        setSurvey={setSurvey}
                        type={question.type}
                        isRequired={question.isRequired}
                        name={question.name}
                        question={question}
                        questionsLength={questionsLength}
                      />
                    );
                  })}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
        <div className='flex mt-5 justify-content-center'>
          <Button className='btn3' label='Save' onClick={() => saveSurvey(survey)} />
          <Button className='btn3 mx-3' label='Preview' onClick={(e) => setShowPreview(true)} />
          <Button className='btn3' label='Publish' onClick={() => publishSurvey(survey)} />
        </div>
      </div>
      <Dialog
        baseZIndex={10001}
        showHeader={true}
        visible={showPreview}
        style={{ width: "600px", maxHeight: "80vh" }}
        onHide={() => {
          setShowPreview(false);
        }}
        closable={true}
        focusOnShow={false}
        className='mi__dialog'
        id='dialog__preview__survey'
      >
        <Preview description={survey.description} questionRequestDTOS={survey.questionRequestDTOS} />
      </Dialog>
      <Dialog
        baseZIndex={10001}
        showHeader={true}
        header='Validation'
        visible={showValidation}
        style={{ width: "300px", maxHeight: "80vh" }}
        onHide={() => {
          setShowValidation(false);
        }}
        closable={true}
        focusOnShow={false}
        // className='mi__dialog'
        id='dialog__add__survey'
      >
        <div style={{ color: "#FFF" }}>Please fill all questions and options</div>

        <div className='p-dialog-footer'>
          <Button className='p-button p-button-sm btn3' label='Ok' onClick={() => setShowValidation(false)}></Button>
        </div>
      </Dialog>
      <Dialog
        baseZIndex={10001}
        showHeader={true}
        header='Confirm'
        visible={isBackHandler}
        style={{ width: "300px", maxHeight: "80vh" }}
        onHide={() => {
          setIsBackHandler(false);
        }}
        closable={true}
        focusOnShow={false}
        // className='mi__dialog'
        id='dialog__add__survey'
      >
        <div style={{ color: "#FFF" }}>Are you sure you want to leave</div>

        <div className='p-dialog-footer flex'>
          <Button className='p-button p-button-sm btn3' label='Cancel' onClick={() => setIsBackHandler(false)}></Button>
          <Button className='p-button p-button-sm btn3' label='Ok' onClick={() => navigate("/survey")}></Button>
        </div>
      </Dialog>
    </>
  );
};

export default SurveyForm;
